import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import AdminNav from "../components/adminNav";
import Time from "../components/time";
import { useParams, Link, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { hasPermission } from "../utils/permissionHelper";
import Access from "../components/access";

/* ========= helpers ========= */

const formatDMY = (dLike) => {
  const d = new Date(dLike);
  if (Number.isNaN(d.getTime())) return "";
  // en-GB already prints dd/mm/yyyy, but we keep explicit:
  return d.toLocaleDateString("en-GB");
};

/* ========= component ========= */

const OwnVehicleConRep = () => {
  useAuthRedirect();
  const { id, branch } = useParams();

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [vehicleBasis, setVehicleBasis] = useState(""); // "Hour" | "Km" | "Ton" | "Month"

  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const startYear = 2007;
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const canView = hasPermission("Vehicle Diesel Consumption", "view");

  // site / company info (for Excel header)
  const [site, setSite] = useState({
    ok: true,
    name: "Infraconsoft",
    logo: null,
    address: "",
    email: "",
    phone: "",
    _ts: 0,
  });

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await axios.get("https://rfs2.infraconsoft.net.in/api/siteinfo");
        const s = res?.data || {};
        let logo = s.logo || null;
        if (logo && !/^https?:\/\//i.test(logo)) {
          if (!logo.startsWith("/")) logo = `/${logo}`;
          logo = `${window.location.origin}${logo}`;
        }
        if (!ignore) {
          setSite({
            ok: !!s.ok,
            name: s.name || "Infraconsoft",
            logo,
            address: s.address || "",
            email: s.email || "",
            phone: s.phone || "",
            _ts: Date.now(),
          });
        }
      } catch (e) {
        console.error("Failed to load /siteinfo", e);
      }
    })();
    return () => { ignore = true; };
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        "https://rfs2.infraconsoft.net.in/api/get_own_veh_report_api",
        { params: { month: selectedMonth, year: selectedYear, branch, id } }
      );

      const responseData = response?.data?.postionData || [];
      setData(responseData);
      setFilteredData(responseData);

      if (responseData.length > 0 && responseData[0].fld_vbasis) {
        setVehicleBasis(String(responseData[0].fld_vbasis));
      } else {
        setVehicleBasis(""); // unknown
      }
    } catch (error) {
      console.error("Error fetching postionData:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = (e.target.value || "").toLowerCase();
    setSearchTerm(term);
    if (!term) {
      setFilteredData(data);
      return;
    }
    const filtered = data.filter((item) =>
      String(item.stn_purnumber || "").toLowerCase().includes(term) ||
      String(item.stn_dpt || "").toLowerCase().includes(term)
    );
    setFilteredData(filtered);
  };

  // Pagination
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  /* ========= Excel export with site header ========= */

  const exportToExcel = () => {
    // We export ALL filtered rows (not just the current page),
    // so monthly download includes complete month.
    let cumulativeKm = 0;
    let cumulativeHours = 0;
    let cumulativeTrips = 0;
    let cumulativeDiesel = 0;

    const isHour = vehicleBasis === "Hour";

    const headers = isHour
      ? [
          "S.No",
          "Date",
          "Working Hours",
          "Cumulative Hours",
          "Number Of Trips",
          "Cumulative Trips",
          "Diesel quantity",
          "Cum. Diesel quantity",
          "Diesel Consumption",
        ]
      : [
          "S.No",
          "Date",
          "Working KM",
          "Cumulative KM",
          "Number Of Trips",
          "Cumulative Trips",
          "Diesel quantity",
          "Cum. Diesel quantity",
          "Diesel Consumption",
        ];

    const exportRows = (filteredData || []).map((item, idx) => {
      const stkm = Number(item.fld_stkm || 0);
      const endkm = Number(item.fld_endkm || 0);
      const wkm = Math.max(0, endkm - stkm); // ensure non-negative

      const noload = Number(item.fld_noload || 0);
      const dieselQty = Number(item.fld_dieselqty || 0);

      cumulativeTrips += noload;
      cumulativeDiesel += dieselQty;

      if (isHour) {
        const workingHours = Number(item.fld_wohrs || 0); // decimal hours
        cumulativeHours += workingHours;
        const dieselConsumption =
          dieselQty > 0 ? workingHours / dieselQty : workingHours; // hrs per liter, fallback to hrs

        return [
          idx + 1,
          formatDMY(item.fld_vdate),
          workingHours.toFixed(2),
          cumulativeHours.toFixed(2),
          noload,
          cumulativeTrips,
          dieselQty.toFixed(2),
          cumulativeDiesel.toFixed(2),
          dieselConsumption.toFixed(2),
        ];
      } else {
        cumulativeKm += wkm;
        const dieselConsumption =
          dieselQty > 0 ? wkm / dieselQty : 0; // km per liter

        return [
          idx + 1,
          formatDMY(item.fld_vdate),
          wkm.toFixed(2),
          cumulativeKm.toFixed(2),
          noload,
          cumulativeTrips,
          dieselQty.toFixed(2),
          cumulativeDiesel.toFixed(2),
          dieselConsumption.toFixed(2),
        ];
      }
    });

    const COLS = headers.length;
    const fullWidth = (text = "") => [text, ...Array(COLS - 1).fill("")];
    const title = `Own Vehicle ${isHour ? "Hours" : "KM"} & Diesel — ${String(
      selectedMonth
    ).padStart(2, "0")}/${selectedYear}`;

    const aoa = [
      fullWidth((site.name || "Infraconsoft").toUpperCase()),
      fullWidth(site.address || ""),
      fullWidth(
        [site.phone ? `Ph: ${site.phone}` : "", site.email ? `Email: ${site.email}` : ""]
          .filter(Boolean)
          .join(" | ")
      ),
      [], // spacer
      fullWidth(title),
      [], // spacer
      headers,
      ...exportRows,
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);

    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: COLS - 1 } }, // name
      { s: { r: 1, c: 0 }, e: { r: 1, c: COLS - 1 } }, // address
      { s: { r: 2, c: 0 }, e: { r: 2, c: COLS - 1 } }, // contact
      { s: { r: 4, c: 0 }, e: { r: 4, c: COLS - 1 } }, // title
    ];

    ws["!cols"] = headers.map((_, i) => {
      if (i === 0) return { wch: 6 };
      if (i === 1) return { wch: 12 };
      return { wch: 18 };
    });

    // Freeze after header block (6 rows before table header line)
    ws["!freeze"] = { xSplit: 0, ySplit: 6, topLeftCell: "A7", activePane: "bottomLeft" };

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Own Vehicle Diesel");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    saveAs(blob, `Own_Vehicle_Diesel_${selectedMonth}-${selectedYear}.xlsx`);
  };

  if (!canView) {
    return (
      <div id="admin-container">
        <AdminNav />
        <div className="content-right" style={{ padding: 20 }}>
          <Access />
        </div>
      </div>
    );
  }

  return (
    <div id="admin-container">
      <AdminNav />
      <div className="content-right">
        <section>
          <div className="back-div">
            <h2 className="add-txt">Own Vehicle Position</h2>
            <Link to={`/vehicle_consumereport_list`}>
              <p className="back-button">
                <i className="fa-solid fa-backward"></i> Back
              </p>
            </Link>
          </div>
          <Time />
        </section>

        <div className="add-new-container">
          <button className="add-new-btn" onClick={exportToExcel}>
            <i className="fa-solid fa-file-excel"></i> Export to Excel
          </button>
        </div>

        {/* Month & Year Selection */}
        <div className="month-year-container">
          <p className="title">Select your Month & Year:</p>
          <div className="select-container">
            <select
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(parseInt(e.target.value, 10));
                setCurrentPage(1);
              }}
              className="custom-select"
            >
              {[...Array(12).keys()].map((i) => (
                <option key={i + 1} value={i + 1}>
                  {(i + 1).toString().padStart(2, "0")}
                </option>
              ))}
            </select>

            <select
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(parseInt(e.target.value, 10));
                setCurrentPage(1);
              }}
              className="custom-select"
            >
              {[...Array(currentYear - startYear + 1).keys()].map((i) => (
                <option key={startYear + i} value={startYear + i}>
                  {startYear + i}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="content-table">
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Date</th>

                {vehicleBasis === "Hour" ? (
                  <>
                    <th>Working Hours</th>
                    <th>Cumulative Hours</th>
                  </>
                ) : (
                  <>
                    <th>Working KM</th>
                    <th>Cumulative KM</th>
                  </>
                )}

                <th>Number Of Trips</th>
                <th>Cumulative Trips</th>
                <th>Diesel quantity</th>
                <th>Cum. Diesel quantity</th>
                <th>Diesel Consumption</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="16" className="loading-text">
                    Loading data, please wait...
                  </td>
                </tr>
              ) : currentData.length > 0 ? (
                (() => {
                  // running totals for the visible page
                  let cumulativeKm = 0;
                  let cumulativeHours = 0;
                  let cumulativeTrips = 0;
                  let cumulativeDiesel = 0;

                  return currentData.map((item, index) => {
                    const stkm = Number(item.fld_stkm || 0);
                    const endkm = Number(item.fld_endkm || 0);
                    const wkm = Math.max(0, endkm - stkm);

                    const noload = Number(item.fld_noload || 0);
                    const dieselQty = Number(item.fld_dieselqty || 0);

                    cumulativeTrips += noload;
                    cumulativeDiesel += dieselQty;

                    let workingCell = "";
                    let cumulativeCell = "";
                    let dieselConsumption = 0;

                    if (item.fld_vbasis === "Hour") {
                      // decimal hours
                      const workingHours = Number(item.fld_wohrs || 0);
                      cumulativeHours += workingHours;
                      workingCell = workingHours.toFixed(2);
                      cumulativeCell = cumulativeHours.toFixed(2);
                      dieselConsumption =
                        dieselQty > 0 ? workingHours / dieselQty : workingHours; // hours/liter
                    } else {
                      // Km/Ton/Month -> KM based view
                      cumulativeKm += wkm;
                      workingCell = wkm.toFixed(2);
                      cumulativeCell = cumulativeKm.toFixed(2);
                      dieselConsumption = dieselQty > 0 ? wkm / dieselQty : 0; // km/liter
                    }

                    return (
                      <tr key={index}>
                        <td>{indexOfFirstItem + index + 1}</td>
                        <td>{formatDMY(item.fld_vdate)}</td>

                        <td className="fontsbillblue">
                          <div align="center">{workingCell}</div>
                        </td>
                        <td className="fontsbillblue">
                          <div align="center">{cumulativeCell}</div>
                        </td>

                        <td className="fontsbillblue">
                          <div align="center">{noload}</div>
                        </td>
                        <td className="fontsbillblue">
                          <div align="center">{cumulativeTrips}</div>
                        </td>
                        <td className="fontsbillblue">
                          <div align="center">{dieselQty.toFixed(2)}</div>
                        </td>
                        <td className="fontsbillblue">
                          <div align="center">{cumulativeDiesel.toFixed(2)}</div>
                        </td>
                        <td className="fontsbillblue">
                          <div align="center">{dieselConsumption.toFixed(2)}</div>
                        </td>
                      </tr>
                    );
                  });
                })()
              ) : (
                <tr>
                  <td colSpan="16">No Data Available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          <button
            onClick={() => paginate(1)}
            className={currentPage === 1 ? "active" : ""}
          >
            1
          </button>

          {filteredData.length > 0 &&
            [...Array(Math.min(3, Math.ceil(filteredData.length / itemsPerPage) - 1))].map(
              (_, index) => {
                const pageNumber = currentPage + index;
                if (
                  pageNumber > 1 &&
                  pageNumber <= Math.ceil(filteredData.length / itemsPerPage)
                ) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => paginate(pageNumber)}
                      className={currentPage === pageNumber ? "active" : ""}
                    >
                      {pageNumber}
                    </button>
                  );
                }
                return null;
              }
            )}

          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={
              currentPage === Math.ceil(filteredData.length / itemsPerPage)
            }
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default OwnVehicleConRep;
